import React, { useState, useEffect, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { toast } from 'sonner';
import { axiosInstance } from '../App';
import { useWtfLiveData } from '../hooks/useWtfLiveData';
import { WtfDataColumn, WtfSyncBadge } from '../components/wtf';

export default function ActiveGyms() {
  const [gyms, setGyms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dateRange, setDateRange] = useState({
    from: getStartOfMonth(),
    to: getTodayDate()
  });

  useEffect(() => {
    fetchMetrics();
  }, [dateRange]);

  const fetchMetrics = async () => {
    setLoading(true);
    try {
      const response = await axiosInstance.get(
        `/active-gyms-metrics?from_date=${dateRange.from}&to_date=${dateRange.to}`
      );
      setGyms(response.data.gyms || []);
    } catch (error) {
      toast.error(error.response?.data?.detail || error.message);
      setGyms([]);
    } finally {
      setLoading(false);
    }
  };

  // WTF Live Data hooks
  // targets: per-gym array [{gym_id, gym_name, daily_target, daily_achieved, remaining_target}]
  // checkin_stats: aggregate object {total_member:"0", total_trainer:"0", ...}
  // member_count: aggregate object {total: 61862}
  const { data: wtfTargets, lastSync: wtfSync, stale: wtfStale, loading: wtfLoading } = useWtfLiveData('targets');
  const { data: wtfCheckinStats } = useWtfLiveData('checkin_stats');
  const { data: wtfMemberCount } = useWtfLiveData('member_count');

  // Aggregate WTF stats (not per-gym)
  const wtfAggregateStats = useMemo(() => {
    const checkins = wtfCheckinStats || {};
    const members = wtfMemberCount || {};
    return {
      totalMembers: parseInt(members.total || '0', 10),
      totalCheckins: parseInt(checkins.total_member || '0', 10) + parseInt(checkins.total_trainer || '0', 10),
    };
  }, [wtfCheckinStats, wtfMemberCount]);

  // Build per-gym WTF lookup from targets feed (the only per-gym data available)
  const wtfGymData = useMemo(() => {
    const lookup = {};
    const targetsArr = Array.isArray(wtfTargets) ? wtfTargets : [];
    targetsArr.forEach(item => {
      const key = item.gym_id || item.gym_name;
      if (key) {
        const target = parseInt(item.daily_target || '0', 10);
        const achieved = parseInt(item.daily_achieved || '0', 10);
        const remaining = parseInt(item.remaining_target || '0', 10);
        const entry = {
          target,
          achieved,
          remaining,
          healthScore: target > 0 ? Math.round((achieved / target) * 100) : null,
        };
        lookup[key] = entry;
        if (item.gym_name && item.gym_name !== key) {
          lookup[item.gym_name] = entry;
        }
      }
    });
    return lookup;
  }, [wtfTargets]);

  // Sort gyms by fulfillment rate (desc), then revenue (desc) as tiebreaker
  const sortedGyms = useMemo(() => {
    return [...gyms].sort((a, b) => {
      if (a.fulfillment_rate !== b.fulfillment_rate) {
        return b.fulfillment_rate - a.fulfillment_rate;
      }
      return b.revenue_from_fulfilled - a.revenue_from_fulfilled;
    });
  }, [gyms]);

  const getRowBackground = (rate) => {
    if (rate > 80) return 'bg-green-50';
    if (rate < 40) return 'bg-red-50';
    return '';
  };

  const resetToMTD = () => {
    setDateRange({
      from: getStartOfMonth(),
      to: getTodayDate()
    });
  };

  // Manager portfolio aggregation
  const managerStats = useMemo(() => {
    const managers = {};
    sortedGyms.forEach(gym => {
      const mgr = gym.manager_name;
      if (!managers[mgr]) {
        managers[mgr] = {
          manager_name: mgr,
          gym_count: 0,
          total_commitments: 0,
          fulfilled_commitments: 0,
          total_revenue: 0,
          gyms: []
        };
      }
      managers[mgr].gym_count += 1;
      managers[mgr].total_commitments += gym.total_commitments;
      managers[mgr].fulfilled_commitments += gym.fulfilled_commitments;
      managers[mgr].total_revenue += gym.revenue_from_fulfilled;
      managers[mgr].gyms.push(gym);
    });

    return Object.values(managers).map(m => {
      // Aggregate WTF target data across manager's gyms
      const wtf_target = m.gyms.reduce((sum, g) => sum + ((wtfGymData[g.gym_id] || wtfGymData[g.gym_name] || {}).target || 0), 0);
      const wtf_achieved = m.gyms.reduce((sum, g) => sum + ((wtfGymData[g.gym_id] || wtfGymData[g.gym_name] || {}).achieved || 0), 0);
      const wtf_remaining = m.gyms.reduce((sum, g) => sum + ((wtfGymData[g.gym_id] || wtfGymData[g.gym_name] || {}).remaining || 0), 0);
      const gymHealthScores = m.gyms
        .map(g => (wtfGymData[g.gym_id] || wtfGymData[g.gym_name] || {}).healthScore)
        .filter(s => s != null);
      const wtf_healthScore = gymHealthScores.length > 0
        ? Math.round(gymHealthScores.reduce((s, v) => s + v, 0) / gymHealthScores.length)
        : null;

      return {
        ...m,
        wtf_target,
        wtf_achieved,
        wtf_remaining,
        wtf_healthScore,
        fulfillment_rate: m.total_commitments > 0
          ? ((m.fulfilled_commitments / m.total_commitments) * 100).toFixed(1)
          : 0
      };
    }).sort((a, b) => {
      // Sort by fulfillment rate desc, then total revenue desc
      if (a.fulfillment_rate !== b.fulfillment_rate) {
        return b.fulfillment_rate - a.fulfillment_rate;
      }
      return b.total_revenue - a.total_revenue;
    });
  }, [sortedGyms, wtfGymData]);

  // TL portfolio aggregation
  const tlStats = useMemo(() => {
    const tls = {};
    sortedGyms.forEach(gym => {
      const tl = gym.tl_name;
      if (!tls[tl]) {
        tls[tl] = {
          tl_name: tl,
          gym_count: 0,
          total_commitments: 0,
          fulfilled_commitments: 0,
          total_revenue: 0,
          gyms: []
        };
      }
      tls[tl].gym_count += 1;
      tls[tl].total_commitments += gym.total_commitments;
      tls[tl].fulfilled_commitments += gym.fulfilled_commitments;
      tls[tl].total_revenue += gym.revenue_from_fulfilled;
      tls[tl].gyms.push(gym);
    });

    return Object.values(tls).map(t => {
      // Aggregate WTF target data across TL's gyms
      const wtf_target = t.gyms.reduce((sum, g) => sum + ((wtfGymData[g.gym_id] || wtfGymData[g.gym_name] || {}).target || 0), 0);
      const wtf_achieved = t.gyms.reduce((sum, g) => sum + ((wtfGymData[g.gym_id] || wtfGymData[g.gym_name] || {}).achieved || 0), 0);
      const wtf_remaining = t.gyms.reduce((sum, g) => sum + ((wtfGymData[g.gym_id] || wtfGymData[g.gym_name] || {}).remaining || 0), 0);
      const gymHealthScores = t.gyms
        .map(g => (wtfGymData[g.gym_id] || wtfGymData[g.gym_name] || {}).healthScore)
        .filter(s => s != null);
      const wtf_healthScore = gymHealthScores.length > 0
        ? Math.round(gymHealthScores.reduce((s, v) => s + v, 0) / gymHealthScores.length)
        : null;

      return {
        ...t,
        wtf_target,
        wtf_achieved,
        wtf_remaining,
        wtf_healthScore,
        fulfillment_rate: t.total_commitments > 0
          ? ((t.fulfilled_commitments / t.total_commitments) * 100).toFixed(1)
          : 0
      };
    }).sort((a, b) => {
      // Sort by fulfillment rate desc, then total revenue desc
      if (a.fulfillment_rate !== b.fulfillment_rate) {
        return b.fulfillment_rate - a.fulfillment_rate;
      }
      return b.total_revenue - a.total_revenue;
    });
  }, [sortedGyms, wtfGymData]);

  return (
    <div className="p-6">
      <div className="flex items-center gap-3 mb-4">
        <h1 className="text-2xl font-bold">Active Gyms Performance</h1>
        <WtfSyncBadge lastSync={wtfSync} stale={wtfStale} />
      </div>

      {/* Date range selector */}
      <Card className="mb-4">
        <CardContent className="pt-4">
          <div className="flex items-center gap-4">
            <div>
              <label className="text-sm font-medium mb-1 block">From</label>
              <Input
                type="date"
                value={dateRange.from}
                onChange={(e) => setDateRange(prev => ({ ...prev, from: e.target.value }))}
                className="w-[160px]"
              />
            </div>
            <div>
              <label className="text-sm font-medium mb-1 block">To</label>
              <Input
                type="date"
                value={dateRange.to}
                onChange={(e) => setDateRange(prev => ({ ...prev, to: e.target.value }))}
                className="w-[160px]"
              />
            </div>
            <Button onClick={resetToMTD} variant="outline" className="mt-6">
              Month to Date
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Tabs: By Gym / By Manager / By TL */}
      <Tabs defaultValue="by-gym">
        <TabsList>
          <TabsTrigger value="by-gym">By Gym</TabsTrigger>
          <TabsTrigger value="by-manager">By Manager</TabsTrigger>
          <TabsTrigger value="by-tl">By TL</TabsTrigger>
        </TabsList>

        <TabsContent value="by-gym">
          <Card>
            <CardHeader>
              <CardTitle>Gym Performance</CardTitle>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="text-center py-8 text-muted-foreground">Loading metrics...</div>
              ) : sortedGyms.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  No active gyms with assigned TL and Manager
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Gym Name</TableHead>
                      <TableHead>TL</TableHead>
                      <TableHead>Manager</TableHead>
                      <TableHead className="text-right">Fulfillment Rate</TableHead>
                      <TableHead className="text-right">Total Revenue (Fulfilled)</TableHead>
                      <TableHead className="text-right">
                        <div>Pulse Completion</div>
                        <div className="text-xs font-normal text-gray-400">Unique pulse slots filled (P1-P4) out of total possible in date range</div>
                      </TableHead>
                      <TableHead className="text-right text-purple-600">WTF Target</TableHead>
                      <TableHead className="text-right text-purple-600">WTF Achieved</TableHead>
                      <TableHead className="text-right text-purple-600">WTF Remaining</TableHead>
                      <TableHead className="text-right text-purple-600">Achievement %</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {sortedGyms.map((gym) => {
                      const wtf = wtfGymData[gym.gym_id] || wtfGymData[gym.gym_name] || {};
                      const achievementColor = wtf.healthScore == null ? 'text-gray-400'
                        : wtf.healthScore >= 90 ? 'text-green-600 font-bold'
                        : wtf.healthScore >= 70 ? 'text-yellow-600 font-semibold'
                        : 'text-red-600 font-bold';
                      return (
                      <TableRow key={gym.gym_id} className={getRowBackground(gym.fulfillment_rate)}>
                        <TableCell className="font-medium">{gym.gym_name}</TableCell>
                        <TableCell>{gym.tl_name}</TableCell>
                        <TableCell>{gym.manager_name}</TableCell>
                        <TableCell className="text-right font-bold">
                          {gym.fulfillment_rate.toFixed(1)}% ({gym.fulfilled_commitments}/{gym.total_commitments})
                        </TableCell>
                        <TableCell className="text-right">
                          ₹{gym.revenue_from_fulfilled.toLocaleString('en-IN')}
                        </TableCell>
                        <TableCell className="text-right">
                          {gym.pulse_completion_rate.toFixed(1)}% ({gym.actual_pulses}/{gym.expected_pulses})
                        </TableCell>
                        <TableCell className="text-right">
                          <WtfDataColumn value={wtf.target} loading={wtfLoading} stale={wtfStale} />
                        </TableCell>
                        <TableCell className="text-right">
                          <WtfDataColumn value={wtf.achieved} loading={wtfLoading} stale={wtfStale} />
                        </TableCell>
                        <TableCell className="text-right">
                          <WtfDataColumn value={wtf.remaining} loading={wtfLoading} stale={wtfStale} />
                        </TableCell>
                        <TableCell className={`text-right ${achievementColor}`}>
                          {wtf.healthScore != null ? `${wtf.healthScore}%` : '--'}
                        </TableCell>
                      </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="by-manager">
          <Card>
            <CardHeader>
              <CardTitle>Manager Portfolio Performance</CardTitle>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="text-center py-8 text-muted-foreground">Loading metrics...</div>
              ) : managerStats.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  No manager data available
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Manager Name</TableHead>
                      <TableHead className="text-right">Gym Count</TableHead>
                      <TableHead className="text-right">Fulfillment Rate</TableHead>
                      <TableHead className="text-right">Total Revenue (Fulfilled)</TableHead>
                      <TableHead className="text-right">Commitments</TableHead>
                      <TableHead className="text-right text-purple-600">WTF Target</TableHead>
                      <TableHead className="text-right text-purple-600">WTF Achieved</TableHead>
                      <TableHead className="text-right text-purple-600">WTF Remaining</TableHead>
                      <TableHead className="text-right text-purple-600">Avg Achievement</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {managerStats.map((manager) => {
                      const mgrHealthColor = manager.wtf_healthScore == null ? 'text-gray-400'
                        : manager.wtf_healthScore >= 90 ? 'text-green-600 font-bold'
                        : manager.wtf_healthScore >= 70 ? 'text-yellow-600 font-semibold'
                        : 'text-red-600 font-bold';
                      return (
                      <TableRow key={manager.manager_name} className={getRowBackground(parseFloat(manager.fulfillment_rate))}>
                        <TableCell className="font-medium">{manager.manager_name}</TableCell>
                        <TableCell className="text-right">{manager.gym_count}</TableCell>
                        <TableCell className="text-right font-bold">
                          {manager.fulfillment_rate}% ({manager.fulfilled_commitments}/{manager.total_commitments})
                        </TableCell>
                        <TableCell className="text-right">
                          ₹{manager.total_revenue.toLocaleString('en-IN')}
                        </TableCell>
                        <TableCell className="text-right">
                          {manager.fulfilled_commitments} / {manager.total_commitments}
                        </TableCell>
                        <TableCell className="text-right">
                          <WtfDataColumn value={manager.wtf_target} loading={wtfLoading} stale={wtfStale} />
                        </TableCell>
                        <TableCell className="text-right">
                          <WtfDataColumn value={manager.wtf_achieved} loading={wtfLoading} stale={wtfStale} />
                        </TableCell>
                        <TableCell className="text-right">
                          <WtfDataColumn value={manager.wtf_remaining} loading={wtfLoading} stale={wtfStale} />
                        </TableCell>
                        <TableCell className={`text-right ${mgrHealthColor}`}>
                          {manager.wtf_healthScore != null ? `${manager.wtf_healthScore}%` : '--'}
                        </TableCell>
                      </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="by-tl">
          <Card>
            <CardHeader>
              <CardTitle>TL Portfolio Performance</CardTitle>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="text-center py-8 text-muted-foreground">Loading metrics...</div>
              ) : tlStats.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  No TL data available
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>TL Name</TableHead>
                      <TableHead className="text-right">Gym Count</TableHead>
                      <TableHead className="text-right">Fulfillment Rate</TableHead>
                      <TableHead className="text-right">Total Revenue (Fulfilled)</TableHead>
                      <TableHead className="text-right">Commitments</TableHead>
                      <TableHead className="text-right text-purple-600">WTF Target</TableHead>
                      <TableHead className="text-right text-purple-600">WTF Achieved</TableHead>
                      <TableHead className="text-right text-purple-600">WTF Remaining</TableHead>
                      <TableHead className="text-right text-purple-600">Avg Achievement</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {tlStats.map((tl) => {
                      const tlHealthColor = tl.wtf_healthScore == null ? 'text-gray-400'
                        : tl.wtf_healthScore >= 90 ? 'text-green-600 font-bold'
                        : tl.wtf_healthScore >= 70 ? 'text-yellow-600 font-semibold'
                        : 'text-red-600 font-bold';
                      return (
                      <TableRow key={tl.tl_name} className={getRowBackground(parseFloat(tl.fulfillment_rate))}>
                        <TableCell className="font-medium">{tl.tl_name}</TableCell>
                        <TableCell className="text-right">{tl.gym_count}</TableCell>
                        <TableCell className="text-right font-bold">
                          {tl.fulfillment_rate}% ({tl.fulfilled_commitments}/{tl.total_commitments})
                        </TableCell>
                        <TableCell className="text-right">
                          ₹{tl.total_revenue.toLocaleString('en-IN')}
                        </TableCell>
                        <TableCell className="text-right">
                          {tl.fulfilled_commitments} / {tl.total_commitments}
                        </TableCell>
                        <TableCell className="text-right">
                          <WtfDataColumn value={tl.wtf_target} loading={wtfLoading} stale={wtfStale} />
                        </TableCell>
                        <TableCell className="text-right">
                          <WtfDataColumn value={tl.wtf_achieved} loading={wtfLoading} stale={wtfStale} />
                        </TableCell>
                        <TableCell className="text-right">
                          <WtfDataColumn value={tl.wtf_remaining} loading={wtfLoading} stale={wtfStale} />
                        </TableCell>
                        <TableCell className={`text-right ${tlHealthColor}`}>
                          {tl.wtf_healthScore != null ? `${tl.wtf_healthScore}%` : '--'}
                        </TableCell>
                      </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}

// Helper functions
function getStartOfMonth() {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  return `${year}-${month}-01`;
}

function getTodayDate() {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}
